import { atom, injectStore, injectAtomInstance, injectAtomValue, injectEffect, injectCallback, api } from '@zedux/react';
import { topicIsLoadingAtom, temporaryTopicAtom, activeTopicAtom } from './atoms';
import { socketAtom } from './socket';
import { IContext, ITopic, IChatJoinEvent, ITopicLastMessage } from '../types';


export const topicsAtom = atom("topics", (context: IContext) => {

    const store = injectStore([] as ITopic[]);
    const temporaryTopicLinksStore = injectStore({} as Record<number, number>);
    const topicIsLoadingInstance = injectAtomInstance(topicIsLoadingAtom);
    const temporaryTopicInstance = injectAtomInstance(temporaryTopicAtom, [context]);
    const activeTopicInstance = injectAtomInstance(activeTopicAtom, [context]);
    const socket = injectAtomValue(socketAtom);

    // function hideOnClickOutside(element: HTMLElement, parent: HTMLElement) {
    //     const outsideClickListener = event => {
    //         if (!element.contains(event.target) && isVisible(element) && panelShown) { // or use: event.target.closest(selector) === null
    //             parent.remove();
    //             store.setState([]);
    //             removeClickListener();
    //             panelShown = false;
    //         }
    //     };

    //     const removeClickListener = () => {
    //         document.removeEventListener('click', outsideClickListener);
    //     };

    //     document.addEventListener('click', outsideClickListener);
    // }


    // TODO: fix this
    const removeTopic = (topic_id: number) => {
        store.setState(topics => topics.filter(t => t.topic_id !== topic_id));
        temporaryTopicLinksStore.setState((state) => Object.fromEntries(
            Object.entries(state).filter(([, realTopicId]) => realTopicId !== topic_id)
        ));
    };

    const updateTopic = injectCallback((topic_id: number, updatedFields: Partial<ITopic>) => {
        store.setState((state) => {
            const topicIndex = state.findIndex(t => t.topic_id === topic_id);
            if (topicIndex !== -1) {
                const updatedTopic = { ...state[topicIndex], ...updatedFields };
                const newState = [...state];
                newState[topicIndex] = updatedTopic;
                return newState;
            }
            return state;
        });
    }, []);

    // Updates the last message details of a topic.
    const updateTopicLastMessage = injectCallback((
        topic_id: number, {
            last_message_text,
            last_message_type,
            last_message_created_at,
            last_message_deleted
        }: ITopicLastMessage) => {
        store.setState((state) => {
            // Find the index of the topic to update its last message details.
            const topicIndex = state.findIndex(t => t.topic_id === topic_id);
            if (topicIndex !== -1) {
                // Check created_at to ensure the last message is more recent than the current one.
                if (last_message_created_at < state[topicIndex].last_message_created_at) {
                    return state;
                }

                const updatedTopic = { ...state[topicIndex], last_message_text, last_message_type, last_message_created_at, last_message_deleted };
                const newState = [...state];
                newState[topicIndex] = updatedTopic;
                return newState;
            }
            return state;
        });
    }, []);

    // Adds a new topic or updates an existing one.
    // If the incoming topic's other_user_id matches the temporary topic's target user, the temporary topic is cleared.
    const addOrUpdateTopic = injectCallback((topic: ITopic) => {
        store.setState((state) => {
            const existingIndex = state.findIndex(t => t.topic_id === topic.topic_id);
            if (existingIndex !== -1) {
                const newState = [...state];
                newState[existingIndex] = { ...newState[existingIndex], ...topic };
                return newState;
            }
            return [topic, ...state];
        });

        const tempTopic = temporaryTopicInstance.getState();
        if (tempTopic && topic.other_user_id !== undefined && tempTopic.temporary_target_user_id === topic.other_user_id) {
            const currentActiveTopicId = activeTopicInstance.getState();
            console.log('Matching temporary topic found. Updating active topic if needed and clearing temporary topic.', { tempTopic, topic, currentActiveTopicId });

            temporaryTopicLinksStore.setState((state) => ({
                ...state,
                [tempTopic.topic_id]: topic.topic_id,
            }));

            // If the user's active topic is the temporary topic, update it to the new topic ID
            if (currentActiveTopicId === tempTopic.topic_id) {
                activeTopicInstance.setState(topic.topic_id);
            }
            temporaryTopicInstance.setState(null);
        }

        // console.log('added or updated topic', topic, store.getState());
    }, []);

    const getRealTopicIdByTemporaryId = injectCallback((temporaryTopicId: number) => {
        return temporaryTopicLinksStore.getState()[temporaryTopicId] ?? null;
    }, []);

    injectEffect(() => {
        if (socket && context) {
            socket.on("topics", (topics) => {
                console.log("received topics ", topics);
                if (!Array.isArray(topics)) return;
                topics.forEach(addOrUpdateTopic)
                topicIsLoadingInstance.setState(false);
            });

            // We set timeout to be sure that the we subscribe to socket events before emitting join, to avoid missing any event emitted right after join
            setTimeout(() => {
                socket.emit("chat:join", { context } as IChatJoinEvent);
            }, 10);

            return () => {
                socket.off("topics");
                socket.emit("chat:leave", { context } as IChatJoinEvent);
            };
        }
    }, [socket, context]);

    return api(store).addExports({ removeTopic, updateTopic, addOrUpdateTopic, getRealTopicIdByTemporaryId, updateTopicLastMessage });
});
